import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["questionKey"];
import { PPUIPageHeader } from 'pricing-pages-ui-components/semantic/PageHeader';
import { PageSection } from 'pricing-pages-ui-components/semantic/PageSection';
import Raven from 'raven-js';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { NavMarker } from 'react-rhumb';
import { getHighlightsForProductLineV2, getProductLevelFromApiNameString } from 'ui-addon-upgrades/_core/features/FeaturesUtils';
import ProductInfoContainer from '../components/ProductInfoContainer';
import BannerSection from '../components/banners/BannerSection';
import ProductPageSubtitle from '../components/common/ProductPageSubtitle';
import ProductPageTitle from '../components/common/ProductPageTitle';
import ValuePropsSection from '../components/common/ValuePropsSection';
import { CreditsCallout } from '../components/common/credits-callout/CreditsCallout';
import CodeSplitEnvWrapper from '../components/common/env/CodeSplitEnvWrapper';
import FallbackErrorBanner from '../components/common/loading/FallbackErrorBanner';
import Faq from '../components/faqs/Faq';
import Question from '../components/faqs/Question';
import ProductFeatureTable from '../components/feature-table/ProductFeatureTable';
import HeaderLayout from '../components/layout/HeaderLayout';
import HubTierInfo from '../components/merch-card/HubTierInfo';
import { PRODUCT_PAGE, PRODUCT_PAGE_CONTENT } from '../constants/ErrorBoundaryNames';
import { DEFAULT, PRICING_AND_PURCHASING_FAILED_STATUS } from '../constants/ErrorCauses';
import { FaqsConfig } from '../constants/faqs/FaqsConfig';
import { getMerchCardsToShow } from '../constants/merchCards/MerchCardsToShow';
import { useAnchorLinkScroll } from '../hooks/htmlElementHooks';
import { useGlobalLoading } from '../hooks/loadingHooks';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { usePageParams } from '../hooks/usePageParams';
import { useTierQueryParam } from '../hooks/useTierQueryParam';
import { hasPricingAndPurchasingFailedSelector } from '../selectors/LoadingStateSelectors';
import { isAppAuthSelector } from '../selectors/AuthSelectors';
import { getNavItemForProductLine } from '../utils/routingUtils';
import { useIsUngatedForPublicRemoveTabs } from '../v2/hooks/gating/useIsUngated';
import { useQueryParamTerm, useQueryParamsEffect } from '../utils/urls/appQueryParamUtilsLegacy';
import { ErrorBoundary } from '../v2/components/error-boundary/ErrorBoundary';
import { ERROR_SEVERITY } from '../v2/components/error-boundary/ErrorSeverity';
import withErrorBoundary from '../v2/components/error-boundary/withErrorBoundary';
import PhoneSupportFooter from '../v2/components/phone-support/PhoneSupportFooter';
import PhoneSupportMiddle from '../v2/components/phone-support/PhoneSupportMiddle';
import { TRACKING_SOURCES } from '../v2/constants/tracking/TrackingSources';
import { IntegrationsSection } from '../v2/containers/IntegrationsSection';
import { trackPageView } from '../v2/utils/tracking/trackingEventUtils';
import ErrorPage from './ErrorPage';
import MissingInfoFallbackPage from './MissingInfoFallbackPage';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ProductPage = ({
  productLine
}) => {
  useAnchorLinkScroll();
  useDocumentTitle(productLine.toLowerCase());
  const isAppReady = useGlobalLoading();
  const isAppAuth = useSelector(isAppAuthSelector);
  const isUngatedForPublicRemoveTabs = useIsUngatedForPublicRemoveTabs();
  const tier = useTierQueryParam();
  const navItem = getNavItemForProductLine(productLine);
  const hasPricingAndPurchasingFailed = useSelector(hasPricingAndPurchasingFailedSelector);
  const {
    tab
  } = usePageParams();
  useEffect(() => {
    if (!navItem) {
      Raven.captureMessage('ProductPage screen is undefined', {
        extra: {
          productLine
        }
      });
      return;
    }
    trackPageView({
      screen: navItem,
      tab
    });
  }, [productLine, navItem, tab]);
  const highlightedProducts = getHighlightsForProductLineV2(productLine);
  const productLevels = getMerchCardsToShow({
    tab,
    tier,
    isUngatedForPublicRemoveTabs: !isAppAuth && isUngatedForPublicRemoveTabs
  });
  const filteredHighlightedProducts = highlightedProducts.filter(apiName => {
    const localProductLevel = getProductLevelFromApiNameString(apiName);
    return productLevels.includes(localProductLevel);
  });
  const {
    queryParamTerm
  } = useQueryParamTerm();
  useQueryParamsEffect({
    term: queryParamTerm
  });
  const faqsConfig = FaqsConfig[navItem];
  if (hasPricingAndPurchasingFailed) {
    return /*#__PURE__*/_jsx(ErrorPage, {
      errorCause: PRICING_AND_PURCHASING_FAILED_STATUS
    });
  }
  const apiName = (filteredHighlightedProducts === null || filteredHighlightedProducts === void 0 ? void 0 : filteredHighlightedProducts.find(name => name.includes('starter'))) || (filteredHighlightedProducts === null || filteredHighlightedProducts === void 0 ? void 0 : filteredHighlightedProducts[0]);
  return /*#__PURE__*/_jsx(ErrorBoundary, {
    fallback: /*#__PURE__*/_jsx(MissingInfoFallbackPage, {
      navItem: navItem
    }),
    errorBoundaryName: PRODUCT_PAGE_CONTENT,
    severity: ERROR_SEVERITY.HIGH,
    metricsOptions: {
      route: navItem
    },
    children: /*#__PURE__*/_jsxs("div", {
      "data-selenium-id": "page-success",
      children: [isAppReady && /*#__PURE__*/_jsx(NavMarker, {
        name: `${productLine}_PAGE`
      }), /*#__PURE__*/_jsx(HeaderLayout, {
        selectedNavItem: navItem,
        children: /*#__PURE__*/_jsxs(ProductInfoContainer, {
          children: [/*#__PURE__*/_jsx(BannerSection, {
            productLine: productLine,
            screen: navItem
          }), /*#__PURE__*/_jsx(FallbackErrorBanner, {}), /*#__PURE__*/_jsxs(PPUIPageHeader, {
            children: [/*#__PURE__*/_jsx(ProductPageTitle, {
              productLine: productLine
            }), /*#__PURE__*/_jsx(ProductPageSubtitle, {
              productLine: productLine
            })]
          }), /*#__PURE__*/_jsx(HubTierInfo, {
            productLine: productLine,
            selectedTab: tab
          }), /*#__PURE__*/_jsx(PageSection, {
            children: /*#__PURE__*/_jsx(CreditsCallout, {
              navItem: navItem
            })
          }), /*#__PURE__*/_jsx(PageSection, {
            children: /*#__PURE__*/_jsx(ProductFeatureTable, {
              productLine: productLine,
              highlightedProducts: filteredHighlightedProducts,
              screen: navItem,
              tab: tab || 'starter'
            }, `${productLine}-${tab || 'starter'}`)
          }), /*#__PURE__*/_jsx(PhoneSupportMiddle, {
            phoneSourceId: TRACKING_SOURCES[`${productLine}_MIDDLE`] || TRACKING_SOURCES.PRODUCT_PAGE,
            screen: navItem,
            apiName: apiName
          }), /*#__PURE__*/_jsx(IntegrationsSection, {}), /*#__PURE__*/_jsx(CodeSplitEnvWrapper, {
            publicComponentLoader: () => import( /* webpackChunkName: "public-chunk" */`pricing-pages-ui/components/company-proof/CompanyProof`),
            publicComponentProps: {
              tab: tab || 'starter'
            }
          }), /*#__PURE__*/_jsx(CodeSplitEnvWrapper, {
            publicComponentLoader: () => import( /* webpackChunkName: "public-chunk" */`pricing-pages-ui/components/case-studies/CaseStudies`),
            publicComponentProps: {
              productLine,
              tab: tab || 'starter'
            }
          }), /*#__PURE__*/_jsx(ValuePropsSection, {}), /*#__PURE__*/_jsx(Faq, {
            children: faqsConfig.map(_ref => {
              let {
                  questionKey
                } = _ref,
                passthroughOptions = _objectWithoutPropertiesLoose(_ref, _excluded);
              return /*#__PURE__*/_jsx(Question, Object.assign({
                questionKey: questionKey
              }, passthroughOptions), questionKey);
            })
          }), /*#__PURE__*/_jsx(PhoneSupportFooter, {
            phoneSourceId: TRACKING_SOURCES[`${productLine}_FOOTER`] || TRACKING_SOURCES.PRODUCT_PAGE,
            screen: navItem,
            apiName: apiName
          })]
        })
      })]
    })
  });
};
export default withErrorBoundary(ProductPage, {
  FallbackComponent: () => /*#__PURE__*/_jsx(ErrorPage, {
    errorCause: DEFAULT
  }),
  errorBoundaryName: PRODUCT_PAGE,
  severity: ERROR_SEVERITY.HIGH
});